export const calculateFinancialHealth = (profile, incomes, expenses, portfolio, loans, reminders) => {
    // ---------------------------------------------------------
    // FINANCIAL HEALTH ALGORITHM (0 - 100)
    // ---------------------------------------------------------

    let score = 0;
    let details = [];

    const totalIncome = incomes.reduce((sum, i) => sum + Number(i.amount), 0);
    const totalExpenses = expenses.reduce((sum, e) => sum + Number(e.amount), 0);
    const totalDebt = loans.reduce((sum, l) => sum + Number(l.outstanding_amount || l.principal), 0);
    const totalInvested = portfolio.reduce((sum, p) => sum + (Number(p.quantity) * Number(p.current_price || p.buy_price)), 0);

    // 0. Check if data exists
    if (totalIncome === 0 && totalExpenses === 0 && totalInvested === 0 && totalDebt === 0) {
        return {
            score: 0,
            details: ["No financial data available. Add income, expenses, or assets to see your health score."]
        };
    }

    // --- 1. 50/30/20 RULE ANALYSIS (Max 30 points) ---
    // Needs: Food, Bills, Rent, Transport, Health, Education
    // Wants: Shopping, Entertainment, Travel
    // Savings: Investments + Surplus
    if (totalIncome > 0) {
        const needsCategories = ['Food', 'Bills', 'Rent', 'Transport', 'Health', 'Education', 'Groceries', 'Utilities'];
        const wantsCategories = ['Shopping', 'Entertainment', 'Travel', 'Dining Out', 'Hobby'];

        const needsExp = expenses.filter(e => needsCategories.includes(e.category) || !wantsCategories.includes(e.category)).reduce((s, e) => s + Number(e.amount), 0);
        const wantsExp = expenses.filter(e => wantsCategories.includes(e.category)).reduce((s, e) => s + Number(e.amount), 0);
        const savingsActual = (totalIncome - totalExpenses) + totalInvested; // Surplus + Portfolio Value (Rough proxy for accumulated savings)

        const needsPct = (needsExp / totalIncome) * 100;
        const wantsPct = (wantsExp / totalIncome) * 100;
        const savingsPct = ((totalIncome - totalExpenses) / totalIncome) * 100;

        // Rule: Needs <= 50%
        if (needsPct <= 50) {
            score += 10;
            details.push("Needs are well managed (<50% of income)");
        } else if (needsPct <= 60) {
            score += 5;
            details.push("Needs are slightly high (>50%)");
        } else {
            details.push("Needs exceed recommended 50% limit");
        }

        // Rule: Wants <= 30%
        if (wantsPct <= 30) {
            score += 10;
            details.push("Discretionary spending is healthy (<30%)");
        } else {
            details.push("Overspending on wants (>30%)");
        }

        // Rule: Savings >= 20%
        if (savingsPct >= 20) {
            score += 10;
            details.push("Excellent savings rate (>20%)");
        } else if (savingsPct >= 10) {
            score += 5;
            details.push("Moderate savings rate (10-20%)");
        } else if (savingsPct > 0) {
            details.push("Savings rate is low (<10%)");
        } else {
            details.push("Expenses exceed Income!");
            score -= 5;
        }

    } else {
        if (totalExpenses > 0) details.push("Tracking expenses but no income source found.");
    }

    // --- 2. DEBT MANAGEMENT (Max 20 points) ---
    // Debt-to-Asset Ratio
    if (totalDebt === 0) {
        score += 20;
        details.push("Calculated as Debt-free");
    } else {
        const assets = totalInvested + (totalIncome * 3); // Approx "Asset Power" (Portfolio + 3 months income power)
        const ratio = assets > 0 ? totalDebt / assets : 100;

        if (ratio < 0.3) {
            score += 15;
            details.push("Debt load is manageable");
        } else if (ratio < 0.6) {
            score += 10;
            details.push("Moderate debt detected");
        } else {
            score += 5;
            details.push("High debt relative to assets");
        }
    }

    // --- 3. LIQUIDITY & EMERGENCY FUND (Max 20 points) ---
    const monthlyBurn = totalExpenses > 0 ? totalExpenses : 20000; // Assume 20k if no expenses logged
    const liquidAssets = portfolio.filter(p => !['Real Estate', 'Land'].includes(p.asset_type))
        .reduce((s, p) => s + (Number(p.quantity) * Number(p.current_price || p.buy_price)), 0);

    const coverageMonths = liquidAssets / monthlyBurn;

    if (coverageMonths >= 6) {
        score += 20;
        details.push("Strong Emergency Fund (6+ months)");
    } else if (coverageMonths >= 3) {
        score += 15;
        details.push("Healthy Emergency Fund (3+ months)");
    } else if (coverageMonths >= 1) {
        score += 10;
        details.push("Basic Emergency Fund (1 month)");
    } else {
        details.push("Low liquidity - Build an emergency fund");
    }

    // --- 4. INVESTMENT & GROWTH (Max 20 points) ---
    if (totalInvested > 0) {
        score += 10;
        const assetTypes = new Set(portfolio.map(p => p.asset_type));
        if (assetTypes.size >= 3) {
            score += 10;
            details.push("Well Diversified Portfolio");
        } else if (assetTypes.size >= 1) {
            score += 5;
            details.push("Started Investing (Consider Diversifying)");
        }
    } else {
        details.push("No investments recorded yet");
    }

    // --- 5. BILL HISTORY (Max 10 points) ---
    const overdueBills = reminders.filter(r => !r.is_completed && new Date(r.due_date) < new Date()).length;
    if (overdueBills === 0) {
        score += 10;
        // details.push("Bills paid on time");
    } else {
        score += Math.max(0, 10 - (overdueBills * 5));
        details.push(`${overdueBills} overdue bills affecting score`);
    }

    // Cap
    score = Math.max(0, Math.min(100, score));

    return { score: Math.round(score), details };
};
